<template>
  <!-- TODO: Manage no elements in `organizationMenuItems` & `organizationMenuBadgeLength` -->
  <Button
    type="button"
    text
    aria-haspopup="true"
    aria-controls="organization_menu"
    class="gap-1"
    :pt="{
      root: 'px-2 py-1 gap-2 w-full text-color',
    }"
    @click="toggleOrganizationMenu"
  >
    <Avatar
      :image="selectedOrganization?.photoURL"
      class="dark:bg-surface-100 shrink-0 overflow-hidden"
    />
    <span
      class="w-full truncate text-left font-bold"
    >
      {{ selectedOrganization?.displayName }}
    </span>
    <Badge
      v-if="organizationMenuBadgeLength > 0"
      :value="organizationMenuBadgeLength"
    />
    <LucideIcon
      :icon="ChevronDown"
      class="shrink-0"
    />
  </Button>

  <TieredMenu
    id="organization_menu"
    ref="organizationMenu"
    :model="organizationMenuItems"
    popup
    :pt="{
      submenu: {
        class: 'overflow-scroll max-h-96',
      },
    }"
  >
    <!-- TODO: Improve -->
    <template #item="{ item, props, hasSubmenu }">
      <router-link
        v-if="item.to"
        v-slot="{ href, navigate }"
        :to="item.to"
        custom
      >
        <a
          v-ripple
          :href="href"
          v-bind="props.action"
          @click="navigate"
        >
          <LucideIcon
            v-if="item.lucideIcon"
            :icon="item.lucideIcon"
          />
          <Avatar
            v-if="item.image"
            :image="item.image"
            shape="circle"
            class="shrink-0"
            style="width: 19px; height: 19px"
          />
          <span>{{ item.label }}</span>
          <Badge
            v-if="item.badge"
            :value="item.badge"
          />
        </a>
      </router-link>
      <div
        v-else-if="item.action"
        v-bind="props.action"
        @click="item.action"
      >
        <LucideIcon :icon="item.lucideIcon" />
        <span>{{ item.label }}</span>
        <Badge
          v-if="item.badge"
          :value="item.badge"
        />
        <LucideIcon
          v-if="hasSubmenu"
          :icon="ChevronRight"
        />
      </div>
      <a
        v-else
        v-ripple
        :href="item.url"
        :target="item.target"
        v-bind="props.action"
      >
        <LucideIcon
          v-if="item.lucideIcon"
          :icon="item.lucideIcon"
        />
        <span>{{ item.label }}</span>
        <Badge
          v-if="item.badge"
          :value="item.badge"
        />
        <LucideIcon
          v-if="hasSubmenu"
          :icon="ChevronRight"
        />
      </a>
    </template>
  </TieredMenu>

  <OrganizationDialog
    :visible="isOrganizationDialogVisible"
    @hide="hideOrganizationDialog"
  />

  <WorkspaceCreationDialog
    :visible="isWorkspaceCreationDialog"
    @hide="hideWorkspaceCreationDialog"
    @created="hideWorkspaceCreationDialog"
  />
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { ArrowLeftRight, ChevronDown, ChevronRight, Earth, Mail, Plus, Settings } from 'lucide-vue-next'
import { collection, query, where } from '@firebase/firestore'
import OrganizationDialog from '~/components/structural/organization/dialog/OrganizationDialog.vue'
import WorkspaceCreationDialog from '~/components/structural/workspace/WorkspaceCreationDialog.vue'
import LucideIcon from '~/components/ui/LucideIcon.vue'
import { useOrganizationsStore } from '~/store/organizations'
import type { Invitation } from '~/types/organizations/invitation'
import { Collections } from '~/constants/firebase'
import { AppRoutes } from '~/constants/routes'
import { OrganizationRoles } from '~/constants/organizations'

const organizationsInvitationsPath = Collections.OrganizationsInvitations
const { data: invitations } = useCollection<Invitation>(
  query(
    collection(useGetFirestore(), organizationsInvitationsPath),
    where('email', '==', (await getCurrentUser()).email),
  ),
  {
    wait: true,
    // Due to issue => https://github.com/vuejs/vuefire/issues/1315
    ssrKey: organizationsInvitationsPath,
  },
)

const organizationsStore = useOrganizationsStore()
const { organizations, selectedOrganization } = storeToRefs(organizationsStore)

const isOrganizationDialogVisible = ref(false)
const showOrganizationDialog = () => {
  isOrganizationDialogVisible.value = true
}
const hideOrganizationDialog = () => {
  isOrganizationDialogVisible.value = false
}

const isWorkspaceCreationDialog = ref(false)
const showWorkspaceCreationDialog = () => {
  isWorkspaceCreationDialog.value = true
}
const hideWorkspaceCreationDialog = () => {
  isWorkspaceCreationDialog.value = false
}

const { t } = useI18n()
const organizationMenu = ref()
const organizationMenuItems = computed(() => {
  const isCurrentUserOrganizationAdmin = isUserGrantedInOrganization(
    OrganizationRoles.Admin,
  )
  const invitationsLength = invitations.value.length
  const switchOrganizationMenuItems = [
    ...(invitationsLength > 0
      ? [
          {
            label: t(
              'layouts.default.app-top-bar.menu-items.organization.items.join',
            ),
            icon: Mail,
            badge: invitationsLength,
            to: AppRoutes.Organization.Invitations,
          },
        ]
      : []),
    ...(currentUserCanCreateOrganization()
      ? [
          {
            label: t(
              'layouts.default.app-top-bar.menu-items.organization.items.create-new',
            ),
            icon: Plus,
            to: AppRoutes.Organization.Create,
          },
        ]
      : []),
    ...organizations.value
      .filter(
        organization => organization.id !== selectedOrganization.value.id,
      )
      .map(organization => ({
        label: organization.displayName,
        image: organization.photoURL,
        to: `/${organization.urlPath}`,
      })),
  ]

  const items = [
    ...getFormattedAppConfigMenuItems('organization'),
    ...(isCurrentUserOrganizationAdmin && mdAndLarger.value
      ? [
          {
            label: 'Settings',
            icon: Settings,
            action: showOrganizationDialog,
          },
        ]
      : []),
    ...(currentUserCanCreateWorkspace() && mdAndLarger.value
      ? [
          {
            label: 'Create a new workspace',
            icon: Earth,
            command: showWorkspaceCreationDialog,
          },
        ]
      : []),
    ...(switchOrganizationMenuItems.length > 0
      ? [
          {
            label: t(
              'layouts.default.app-top-bar.menu-items.organization.label',
            ),
            icon: ArrowLeftRight,
            badge: invitationsLength,
            items: switchOrganizationMenuItems,
          },
        ]
      : []),
  ]

  // Convert `icon` to `lucideIcon` for `TieredMenu` because it breaks if `icon` is used
  const convertIconsRecursively = (items) => {
    return items.map(({ icon, ...item }) => ({
      ...item,
      ...(icon && { lucideIcon: icon }),
      ...(item.items && { items: convertIconsRecursively(item.items) }),
    }))
  }

  return convertIconsRecursively(items)
})
const toggleOrganizationMenu = (event) => {
  organizationMenu.value.toggle(event)
}
const organizationMenuBadgeLength = computed(() => {
  return invitations.value.length
})
</script>
